export const IMAGES = {
  workspace: "https://images.unsplash.com/photo-1650661926447-9efb2610f64c?auto=format&fit=crop&w=1400&q=82",
  building: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=1400&q=82",
  dusk: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1400&q=82",
  laptop: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=82",
  restaurant: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1400&q=82",
  construction: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5c?auto=format&fit=crop&w=1400&q=82",
  suit: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=1400&q=82",
};

export const SERVICES = [
  {
    slug: "websites",
    path: "/services/websites",
    index: "01",
    title: "Website Design & Development",
    short: "Premium custom websites with strong structure, responsive design, performance and the polish your business should be known for.",
    heroTitle: "Websites people remember.",
    heroLead: "We plan, design and build custom websites around your business — not around a template. Clear structure, premium art direction, strong responsive composition across mobile, tablet, laptop and desktop, with performance from day one.",
    includes: [
      "Custom visual direction and responsive page design",
      "Modern React development with accessible, semantic markup",
      "Mobile, tablet, laptop and desktop composition and interaction design",
      "Performance optimisation and technical QA",
      "Contact, enquiry, map and practical integrations",
      "Analytics, metadata and structured data setup",
    ],
    detail: [
      "Every engagement starts with structure: what customers need to understand, in what order, and what action each page should earn. The design then gives that logic a memorable visual identity.",
      "The finished site is built to feel premium without sacrificing speed, accessibility or maintainability. Motion and 3D are used when they add to the story — not just because they can be added.",
    ],
    outcomes: [
      "A distinctive premium website",
      "Strong mobile, tablet, laptop and desktop presentation",
      "A clear conversion path for enquiries",
      "A maintainable platform ready to grow",
    ],
    image: IMAGES.workspace,
  },
  {
    slug: "seo",
    path: "/services/seo",
    index: "02",
    title: "SEO Optimisation",
    short: "Technical and editorial SEO built around clear structure, useful content and the searches that matter to your customers.",
    heroTitle: "Get found for the right reasons.",
    heroLead: "We improve the foundations that make a website easier to discover: technical health, page structure, metadata, local signals and content aligned to genuine search intent.",
    includes: [
      "Technical SEO review: crawlability, speed, structure and indexing",
      "Keyword and intent research",
      "On-page titles, headings, internal links and metadata",
      "Local and service-area optimisation where relevant",
      "Content structure and editorial recommendations",
      "Plain-English reporting and ongoing refinement",
    ],
    detail: [
      "Good SEO starts with a useful website. We fix technical and structural issues first, then improve the content and signals around the searches that matter to the business.",
      "We do not promise rankings. We build the conditions for stronger organic visibility and explain what is changing along the way.",
    ],
    outcomes: [
      "Cleaner technical search foundations",
      "Pages aligned to customer intent",
      "Better local and organic discoverability",
      "Reporting you can understand",
    ],
    image: IMAGES.building,
  },
  {
    slug: "ai-search",
    path: "/services/ai-search",
    index: "03",
    title: "AI Search Optimisation",
    short: "Structure your business information so modern search assistants can better understand what you do, who you serve and where your expertise fits.",
    heroTitle: "Be easier for AI systems to understand.",
    heroLead: "AI assistants like ChatGPT and Google’s AI answers increasingly assemble responses from structured, specific and trustworthy sources. In plain English: we make it easy for them to understand who you are, what you do and where, so they can describe your business accurately. We can’t guarantee a mention, and we won’t pretend anyone can.",
    includes: [
      "Clear service, location and brand entity information",
      "Structured content that answers specific customer questions",
      "Schema and machine-readable business data",
      "Consistent terminology across important pages",
      "Question-led content planning",
      "Ongoing review as AI-search behaviour changes",
    ],
    detail: [
      "AI-search optimisation is primarily about clarity. We structure content so systems can better identify the business, its services, locations, expertise and relevant answers.",
      "No provider can honestly guarantee inclusion in AI answers. The goal is to make the site more legible, specific and useful to both people and machines.",
    ],
    outcomes: [
      "Clearer machine-readable business information",
      "Content that is easier to quote and summarise",
      "A stronger foundation for emerging search behaviour",
      "One search strategy spanning traditional and AI search",
    ],
    image: IMAGES.dusk,
  },
  {
    slug: "hosting-support",
    path: "/services/support",
    index: "04",
    title: "Managed Hosting & Website Care",
    short: "Deployment, DNS, SSL, caching, monitoring, backups, analytics and ongoing updates — the practical web infrastructure behind a healthy site.",
    heroTitle: "Launch is the beginning.",
    heroLead: "We manage the practical infrastructure around the websites we build, so you are not left coordinating hosting, DNS, certificates, monitoring and updates across different providers.",
    includes: [
      "Managed website deployment and hosting configuration",
      "Domain and DNS connection support",
      "SSL/TLS certificate setup and renewal support",
      "Uptime and basic performance monitoring",
      "Backups and restore planning appropriate to the site",
      "Caching, CDN and performance configuration",
      "Analytics setup and minor content updates depending on plan",
      "Ongoing technical support from the team managing the site",
    ],
    detail: [
      "This is managed website infrastructure — not enterprise AWS, Azure or Kubernetes consulting. We handle the web-hosting layer needed to keep the sites we build available, secure, fast and maintainable.",
      "That includes deployment, DNS, SSL, caching, monitoring, backups and routine website care. If a project requires specialist enterprise cloud architecture, we scope that separately rather than pretending it is part of a standard website package.",
    ],
    outcomes: [
      "One team accountable after launch",
      "Monitoring and routine website care",
      "DNS, SSL and hosting handled in one place",
      "A site that stays current instead of being forgotten",
    ],
    image: IMAGES.laptop,
  },
  {
    slug: "automation",
    path: "/services/automation",
    index: "05",
    title: "Automation & Integrations",
    short: "Connect your website to the tools your business already uses — lead routing, CRM sync, booking flows, notifications and practical workflow automation.",
    heroTitle: "Less admin. Better flow.",
    heroLead: "Your website should work with the rest of your business. We connect enquiries, forms and customer actions to the systems you already use, reducing manual handoffs and keeping information moving.",
    includes: [
      "Lead capture routed to the right inbox, CRM or workflow",
      "Booking, enquiry and intake workflow automation",
      "Custom API and webhook integrations",
      "Automated notifications and follow-up sequences",
      "Data sync between website and business systems",
      "Clear documentation so workflows remain understandable",
    ],
    detail: [
      "We start with the manual handoffs around your website: where enquiries arrive, who needs them, what information is copied between systems and where follow-up can stall.",
      "We then connect the useful parts with practical, maintainable automation. The goal is not automation for its own sake — it is fewer repetitive steps, cleaner lead handling and systems that work together reliably.",
    ],
    outcomes: [
      "Fewer manual handoffs",
      "Faster, more consistent lead routing",
      "Connected website and business systems",
      "Documented workflows your team can understand",
    ],
    image: IMAGES.suit,
  },
];

export const CAPABILITY_TAGS = [
  "Website Design & Development",
  "SEO Optimisation",
  "AI Search Optimisation",
  "Managed Hosting",
  "DNS & SSL Support",
  "Performance Optimisation",
  "Uptime Monitoring",
  "Analytics & Reporting",
  "Content Updates",
  "Ongoing Support",
  "Automation & Integrations",
];

export const PROCESS = [
  { step: "01", name: "Discover", text: "We learn the business, customers and goals before design begins." },
  { step: "02", name: "Plan", text: "Structure, content and technical approach are mapped before production." },
  { step: "03", name: "Design", text: "We design the look and layout around your brand, then refine it with your feedback." },
  { step: "04", name: "Build", text: "The approved direction becomes a fast, responsive working site." },
  { step: "05", name: "Launch", text: "We test it on phones and desktops, connect your contact form and publish it." },
  { step: "06", name: "Thrive", text: "SEO, AI-search structure, automation, hosting and website care continue after launch." },
];

export const PROJECTS = [
  {
    id: "sora-dining",
    label: "Concept Project",
    name: "Sora Dining",
    industry: "Hospitality",
    scope: ["Website Design", "Mobile Experience", "Local SEO"],
    summary: "A restaurant concept built around appetite, atmosphere and fast decision-making — strong visual storytelling without burying menus, location or booking intent.",
    approach: "Cinematic motion is used where it adds desire, while the information architecture stays simple: venue, menu, atmosphere, location and enquiry actions remain obvious on every device.",
    image: IMAGES.restaurant,
    palette: { bg: "#16110D", surface: "#241A13", accent: "#C9955A", text: "#F7EFE4" },
    url: "sora-dining.concept",
  },
  {
    id: "northline-build",
    label: "Concept Project",
    name: "Northline Build",
    industry: "Construction & Trades",
    scope: ["Website Design", "Service Structure", "SEO"],
    summary: "A construction concept designed to turn credibility into enquiries through clear services, project proof, responsive presentation and strong local-search foundations.",
    approach: "The site balances premium presentation with practical proof: project imagery, service areas, capabilities and contact paths are easy to understand without looking like a generic trades template.",
    image: IMAGES.construction,
    palette: { bg: "#111313", surface: "#1A1E1D", accent: "#C49A62", text: "#F4F0E8" },
    url: "northline-build.concept",
  },
  {
    id: "arden-advisory",
    label: "Internal Showcase",
    name: "Arden Advisory",
    industry: "Professional Services",
    scope: ["Website Design", "SEO Structure", "AI Search"],
    summary: "A professional-services concept focused on making complex expertise easier to trust, navigate and understand across traditional and AI-assisted search.",
    approach: "Editorial typography, concise service pages and structured answers make the firm feel credible without relying on generic corporate imagery or invented proof.",
    image: IMAGES.suit,
    palette: { bg: "#11110F", surface: "#1D1B17", accent: "#B88B55", text: "#F4EFE6" },
    url: "arden-advisory.showcase",
  },
];

export const NAV_LINKS = [
  { label: "Home", path: "/", testId: "home" },
  { label: "Services", path: "/services", testId: "services" },
  { label: "Our Work", path: "/work/khim-thai-restaurant", testId: "our-work" },
  { label: "Concepts", path: "/concepts", testId: "concepts" },
  { label: "Pricing", path: "/services/website-packages", testId: "website-packages" },
  { label: "About", path: "/about", testId: "about" },
];

export const SERVICE_OPTIONS = [
  "Website",
  "SEO",
  "AI Search",
  "Hosting / Website Care",
  "Automation / Integrations",
  "Not Sure Yet",
];
